import { useEffect, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

interface ScrollAreaProps {
  children: ReactNode;
  className?: string;
  /** Классы внутренней обёртки — по ней же меряем высоту контента. */
  contentClassName?: string;
  /** Атрибуты внутренней обёртки: она же контейнер сетки, поэтому role/aria вешать надо на неё. */
  contentProps?: Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'>;
}

interface Edges {
  top: boolean;
  bottom: boolean;
}

/**
 * Область между вопросом и кнопкой «Продолжить»: забирает всю оставшуюся высоту
 * и скроллит контент внутри себя, чтобы длинный список не растягивал экран и
 * футер не прыгал от шага к шагу. Требует родителя с ограниченной высотой и
 * `flex-col`.
 *
 * Скроллбар в мини-аппе скрыт (см. base.css), поэтому единственная подсказка о
 * том, что список продолжается, — растворяющаяся кромка. Показываем её только с
 * той стороны, где реально есть что доскроллить: иначе крайний пункт выглядел бы
 * выцветшим без причины.
 */
export function ScrollArea({
  children,
  className,
  contentClassName,
  contentProps,
}: ScrollAreaProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<Edges>({ top: false, bottom: false });

  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const update = () => {
      const top = viewport.scrollTop > 1;
      const bottom = viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > 1;
      setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    };
    update();

    viewport.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    observer.observe(content);
    return () => {
      viewport.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  const mask = useMemo(() => {
    if (!edges.top && !edges.bottom) return undefined;
    const from = edges.top ? 'transparent 0, #000 24px' : '#000 0';
    const to = edges.bottom ? '#000 calc(100% - 32px), transparent 100%' : '#000 100%';
    const image = `linear-gradient(to bottom, ${from}, ${to})`;
    return { maskImage: image, WebkitMaskImage: image };
  }, [edges]);

  return (
    <div
      ref={viewportRef}
      className={cn('scroll-area min-h-0 flex-1 overflow-y-auto overscroll-contain', className)}
      style={mask}
    >
      <div ref={contentRef} className={contentClassName} {...contentProps}>
        {children}
      </div>
    </div>
  );
}
